import { createSelector } from '@reduxjs/toolkit';
import { getAuthToken } from './user';
import { getDeployment } from './config';
import { getProcessingFees } from '@/redux/api/payment';
import { GlobalState } from '../rootReducer';
import { ProcessingFees } from '@/types/ProcessingFees';
import cloneDeep from 'lodash/cloneDeep';
import difference from 'lodash/difference';
import isEmpty from 'lodash/isEmpty';
import ms from 'ms';
import union from 'lodash/union';

/* Action Types */
export const LOAD_PROCESSING_FEE_REQUEST = 'la/ui/processingFee/LOAD_PROCESSING_FEE_REQUEST';
export const LOAD_PROCESSING_FEE_SUCCESS = 'la/ui/processingFee/LOAD_PROCESSING_FEE_SUCCESS';
export const LOAD_PROCESSING_FEE_FAIL = 'la/ui/processingFee/LOAD_PROCESSING_FEE_FAIL';

const REDUX_STORE_TIME = ms('30m');

export type State = {
    byCatalogId: { [catalogId: number]: any };
    loaded: { [catalogId: number]: number };
    loading: number[];
    uiState: {
        error: string;
        loading: boolean;
        success: boolean;
    };
};

export const DEFAULT_STATE: State = {
    byCatalogId: {},
    loaded: {},
    loading: [],
    uiState: {
        error: '',
        loading: false,
        success: false,
    },
};

export default function reducer(state: State = DEFAULT_STATE, action: any = {}): State {
    let existing;
    let loaded;
    let loading;
    let time;

    switch (action.type) {
        case LOAD_PROCESSING_FEE_FAIL:
            return {
                ...state,
                loading: difference(state.loading, [action.meta.catalogId]),
                uiState: {
                    ...state.uiState,
                    error: 'Failed to load processing fees',
                    loading: false,
                    success: false,
                },
            };
        case LOAD_PROCESSING_FEE_REQUEST:
            return {
                ...state,
                loading: union(state.loading, [action.payload]),
                uiState: { ...state.uiState, error: '', loading: true, success: false },
            };
        case LOAD_PROCESSING_FEE_SUCCESS:
            time = action.meta.actionTime;
            existing = cloneDeep(state.byCatalogId);
            loaded = { ...state.loaded, [action.meta.catalogId]: time };
            loading = cloneDeep(state.loading);
            existing[action.meta.catalogId] = action.payload;
            loading = difference(loading, [action.meta.catalogId]);
            return {
                ...state,
                byCatalogId: existing,
                loaded,
                loading,
                uiState: { ...state.uiState, error: '', loading: false, success: true },
            };
        default:
            return state;
    }
}

/* SELECTORS */
const stateSelector = (state: GlobalState) => state.processingFee;
const idSelector = (state: GlobalState, id) => id;

const byIdSelector = createSelector(stateSelector, (state) => state.byCatalogId);

const loadedSelector = createSelector(stateSelector, (state) => state.loaded);

const loadingSelector = createSelector(stateSelector, (state) => state.loading);

export const getFees: (state: GlobalState, catalogId: number) => ProcessingFees = createSelector(
    [byIdSelector, idSelector],
    (byId, id) =>
        byId[id] || { achFee: 0, balanceWithACHFee: 0, balanceWithCCFee: 0, balanceWithHRFee: 0, ccFee: 0, hrFee: 0 }
);
export const getFeeError = createSelector(stateSelector, (localState) => localState.uiState.error);
export const getFeeLoadSuccess = createSelector(stateSelector, (localState) => localState.uiState.success);
export const getFeeLoading = createSelector(stateSelector, (localState) => localState.uiState.loading);

const getLoadTimeForFees = createSelector([loadedSelector, idSelector], (loaded, id) => loaded[id] || 0);
const isFeesLoading = createSelector([loadingSelector, idSelector], (loading, id) => loading.includes(id));

const shouldFetchFees = createSelector(
    [idSelector, getFees, getLoadTimeForFees, isFeesLoading],
    (catalogId, invoice, loaded, loading) => {
        if (!catalogId) {
            return false;
        }
        if (!isEmpty(invoice)) {
            const time = Date.now();
            const diff = time - loaded;
            if (diff < REDUX_STORE_TIME) {
                return false;
            }
        }
        return !loading;
    }
);

/* ACTION CREATORS */
export const fetchProcessingFeesIfNeeded =
    (catalogId: number, balanceDue: number, force: boolean = false) =>
    async (dispatch: Function, getState: Function) => {
        const state = getState();
        if (shouldFetchFees(state, catalogId) || force) {
            try {
                const authToken = getAuthToken(state);
                const deployment = getDeployment(state);
                dispatch({
                    meta: { actionTime: Date.now() },
                    payload: catalogId,
                    type: LOAD_PROCESSING_FEE_REQUEST,
                });
                // @ts-ignore
                const balanceDueCents = Math.round(parseFloat(Number(balanceDue)) * 100);
                const response = await getProcessingFees({
                    authToken,
                    balanceDue: balanceDueCents,
                    catalogId,
                    deployment,
                });
                dispatch({
                    meta: { actionTime: Date.now(), catalogId },
                    payload: response.payload,
                    type: LOAD_PROCESSING_FEE_SUCCESS,
                });
            } catch (error) {
                dispatch({
                    error: true,
                    meta: { catalogId },
                    payload: error,
                    type: LOAD_PROCESSING_FEE_FAIL,
                });
            }
        } else {
            return Promise.resolve();
        }
    };
